/**
 * 用户管理 API
 * 对应 foundation-service (7092)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';
import type {
  User,
  UserQueryParams,
  UserFormData,
  UserStatus,
  UserStatistics,
  UserImportResponse,
  UploadImageResponse,
  UserChangeLog,
  UserChangeLogParams,
  TransferUserData,
  UserRoleWithOrg,
  AssignRoleWithOrgData,
  UserOrganizationInfo,
  BatchAddToOrgData,
} from './types';
import { toUserListRequestParams } from './userListParams';

/** 获取用户列表 */
export const getUserList = async (params: UserQueryParams = {}) => {
  return handleApiResponse(
    unifiedApi.service('user').get<{ data: User[]; count: number }>('/users', {
      params: toUserListRequestParams(params),
    })
  );
};

/** 获取用户详情 */
export const getUserById = async (id: string) => {
  return handleApiResponse(
    unifiedApi.service('user').get<{ data: User; count: number }>(`/users/${id}`)
  );
};

/** 创建用户 */
export const createUser = async (data: UserFormData) => {
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: User; count: number }>('/users', data)
  );
};

/** 更新用户 */
export const updateUser = async (id: string, data: Partial<UserFormData>) => {
  return handleApiResponse(
    unifiedApi.service('user').put<{ data: User; count: number }>(`/users/${id}`, data)
  );
};

/** 删除用户 */
export const deleteUser = async (id: string) => {
  return handleApiResponse(
    unifiedApi.service('user').delete<{ data: null; count: number }>(`/users/${id}`)
  );
};

/** 重置用户密码 */
export const resetUserPassword = async (id: string, password: string) => {
  return handleApiResponse(
    unifiedApi.service('user').post<{ new_password: string }>(`/users/${id}/reset-password`, { new_password: password })
  );
};

/** 更新用户状态 */
export const updateUserStatus = async (id: string, status: UserStatus) => {
  return handleApiResponse(
    unifiedApi.service('user').put<{ data: null; count: number }>(`/users/${id}/status`, { status })
  );
};

/** 批量删除用户 */
export const batchDeleteUsers = async (ids: string[]) => {
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: null; count: number }>('/user/batch/delete', { ids })
  );
};

/** 批量更新用户状态 */
export const batchUpdateUserStatus = async (ids: string[], status: UserStatus) => {
  return handleApiResponse(
    unifiedApi.service('user').put<{ data: null; count: number }>('/user/batch/status', { ids, status })
  );
};

/** 获取用户统计 */
export const getUserStatistics = async () => {
  return handleApiResponse(
    unifiedApi.service('user').get<{ data: UserStatistics; count: number }>('/users/statistics')
  );
};

/** 检查用户名是否存在 */
export const checkUsernameExists = async (username: string, excludeId?: string) => {
  return handleApiResponse(
    unifiedApi.service('user').get<{ data: { available: boolean; suggestion?: string }; count: number }>(
      '/user/check-username',
      { params: { username, excludeId } }
    )
  );
};

/** 根据用户名搜索用户 */
export const getUserByName = async (username: string) => {
  return handleApiResponse(
    unifiedApi.service('user').get<{ data: User[]; count: number }>('/user/search', { params: { username } })
  );
};

/** 下载用户导入模板 */
export const downloadUserImportTemplate = async () => {
  const response = await unifiedApi.service('user').get('/user/import/template', {
    responseType: 'blob',
  });
  return response.data;
};

/** 导入用户 */
export const importUsers = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: UserImportResponse; count: number }>(
      '/user/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  );
};

/** 上传图片 */
export const uploadImage = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: UploadImageResponse; count: number }>(
      '/upload/image',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  );
};

/** 更新用户头像 */
export const updateUserAvatar = async (avatar: string) => {
  return handleApiResponse(
    unifiedApi.service('user').put<{ data: null; count: number }>('/user/self/avatar', { avatar })
  );
};

// ==================== 用户详情补全 API ====================

/** 获取用户变更日志 */
export const getUserChangeLogs = async (userId: string, params?: UserChangeLogParams) => {
  return handleApiResponse(
    unifiedApi.service('user').get<{ list: UserChangeLog[]; total: number }>(
      `/users/${userId}/change-logs`, { params }
    )
  );
};

/** 用户调岗 */
export const transferUser = async (userId: string, data: TransferUserData) => {
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: null }>(`/users/${userId}/transfer`, data)
  );
};

/** 获取用户组织维度角色 */
export const getUserRolesWithOrg = async (userId: string) => {
  return handleApiResponse(
    unifiedApi.service('user').get<{ list: UserRoleWithOrg[] }>(`/users/${userId}/roles-with-org`)
  );
};

/** 分配用户组织维度角色 */
export const assignUserRolesWithOrg = async (userId: string, data: AssignRoleWithOrgData) => {
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: null }>(`/users/${userId}/roles-with-org`, data)
  );
};

/** 移除用户组织维度角色 */
export const removeUserRolesWithOrg = async (userId: string, roleId: string, orgId: string) => {
  return handleApiResponse(
    unifiedApi.service('user').delete<{ data: null }>(`/users/${userId}/roles-with-org`, {
      data: { role_id: roleId, org_id: orgId },
    })
  );
};

/** 获取用户所属组织列表 */
export const getUserOrgs = async (userId: string) => {
  return handleApiResponse(
    unifiedApi.service('user').get<{ list: UserOrganizationInfo[] }>(`/users/${userId}/organizations`)
  );
};

/** 添加用户到组织 */
export const addUserToOrg = async (userId: string, orgId: string) => {
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: null }>(`/users/${userId}/organizations`, { org_id: orgId })
  );
};

/** 从组织移除用户 */
export const removeUserFromOrg = async (userId: string, orgId: string) => {
  return handleApiResponse(
    unifiedApi.service('user').delete<{ data: null }>(`/users/${userId}/organizations/${orgId}`)
  );
};

/** 设置用户主组织 */
export const setUserPrimaryOrg = async (userId: string, orgId: string) => {
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: null }>(`/users/${userId}/organizations/set-primary`, { org_id: orgId })
  );
};

/** 批量添加用户到组织 */
export const batchAddUsersToOrg = async (data: BatchAddToOrgData) => {
  return handleApiResponse(
    unifiedApi.service('user').post<{ data: null }>('/users/batch-add-to-org', data)
  );
};

/** 用户 API 对象 */
export const userApi = {
  getUserList,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  updateUserStatus,
  batchDeleteUsers,
  batchUpdateUserStatus,
  getUserStatistics,
  checkUsernameExists,
  getUserByName,
  downloadUserImportTemplate,
  importUsers,
  uploadImage,
  updateUserAvatar,
  getUserChangeLogs,
  transferUser,
  getUserRolesWithOrg,
  assignUserRolesWithOrg,
  removeUserRolesWithOrg,
  getUserOrgs,
  addUserToOrg,
  removeUserFromOrg,
  setUserPrimaryOrg,
  batchAddUsersToOrg,
};

export default userApi;
